/**
 * Silva Kitchen — Interactive JS
 * Vanilla JS only. Degrades gracefully if metafields missing or customer not logged in.
 */

(function () {
  'use strict';

  /* ================================================================
     FILTER PILLS
     ================================================================ */
  function initFilterPills() {
    const container = document.querySelector('[aria-label="Filter recipes"]');
    if (!container) return;

    const pills = container.querySelectorAll('.sk-filters__pill');
    const cards = document.querySelectorAll('.sk-recipe-card');

    function applyFilter(filter) {
      pills.forEach(p => {
        p.classList.toggle('is-active', p.dataset.filter === filter);
        p.setAttribute('aria-pressed', p.dataset.filter === filter ? 'true' : 'false');
      });

      cards.forEach(card => {
        const matches =
          filter === 'all' ||
          card.dataset.occasion === filter ||
          card.dataset.dishType === filter;
        card.hidden = !matches;
      });

      const url = new URL(window.location.href);
      if (filter === 'all') {
        url.searchParams.delete('filter');
      } else {
        url.searchParams.set('filter', filter);
      }
      history.replaceState(null, '', url.toString());
    }

    pills.forEach(pill => {
      pill.setAttribute('aria-pressed', pill.classList.contains('is-active') ? 'true' : 'false');
      pill.addEventListener('click', () => applyFilter(pill.dataset.filter));
    });

    const initialFilter = new URLSearchParams(window.location.search).get('filter') || 'all';
    applyFilter(initialFilter);
  }

  /* ================================================================
     IOTM CTA — scrolls to recipes and activates linked filter
     ================================================================ */
  function initIotmCta() {
    const cta = document.querySelector('.sk-iotm__cta[data-filter]');
    if (!cta) return;

    cta.addEventListener('click', e => {
      e.preventDefault();
      const filter = cta.dataset.filter;
      const target = document.querySelector(`.sk-filters__pill[data-filter="${filter}"]`);
      if (target) {
        target.click();
        document.getElementById('recipes')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  /* ================================================================
     SERVES ADJUSTER
     ================================================================ */
  function initServesAdjuster() {
    const adjuster = document.querySelector('.rd-serves');
    if (!adjuster) return;

    const countEl = adjuster.querySelector('.rd-serves__count');
    if (!countEl) return;

    const baseServes = parseInt(countEl.dataset.base, 10) || 4;
    let current = baseServes;

    const ingredients = document.querySelectorAll('.rd-ingredient[data-qty]');

    function parseQty(str) {
      if (!str) return null;
      // Handle fractions: "1/2"
      const frac = str.trim().match(/^(\d+)\/(\d+)\s*(.*)$/);
      if (frac) return { num: parseInt(frac[1], 10) / parseInt(frac[2], 10), unit: frac[3].trim() };
      // Handle decimals and integers: "600g", "2 tbsp"
      const match = str.trim().match(/^([\d.]+)\s*(.*)$/);
      if (!match) return null;
      return { num: parseFloat(match[1]), unit: match[2].trim() };
    }

    function formatNum(n) {
      if (n === Math.round(n)) return String(Math.round(n));
      return (Math.round(n * 10) / 10).toFixed(1).replace(/\.0$/, '');
    }

    adjuster.querySelectorAll('.rd-serves__btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const dir = parseInt(btn.dataset.direction, 10);
        const next = Math.max(1, current + dir);
        if (next === current) return;
        current = next;
        countEl.textContent = current;

        const multiplier = current / baseServes;
        ingredients.forEach(ing => {
          const parsed = parseQty(ing.dataset.qty);
          if (!parsed) return;
          const scaled = parsed.num * multiplier;
          const qtyEl = ing.querySelector('.rd-ingredient__quantity');
          if (qtyEl) qtyEl.textContent = formatNum(scaled) + (parsed.unit ? ' ' + parsed.unit : '');
        });
      });
    });
  }

  /* ================================================================
     LIKES
     ================================================================ */
  function initLikes() {
    const STORAGE_KEY = 'sk_liked';
    let liked = {};
    try { liked = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch (_) {}

    function saveLiked() {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(liked)); } catch (_) {}
    }

    // Detail page like button
    const detailBtn = document.querySelector('.rd-btn--like');
    if (detailBtn) {
      const wrapper = detailBtn.closest('[data-recipe-id]');
      const recipeId = wrapper?.dataset.recipeId;
      const countEl = detailBtn.querySelector('.rd-btn__likes-count');
      const base = parseInt(detailBtn.dataset.baseLikes, 10) || 0;

      function updateDetailLike(isLiked) {
        detailBtn.setAttribute('aria-pressed', isLiked ? 'true' : 'false');
        if (countEl) countEl.textContent = base + (isLiked ? 1 : 0);
      }

      if (recipeId) {
        updateDetailLike(!!liked[recipeId]);
        detailBtn.addEventListener('click', () => {
          const next = !liked[recipeId];
          liked[recipeId] = next;
          saveLiked();
          updateDetailLike(next);
        });
      }
    }

    // Sync counts on recipe cards (display-only, reflects localStorage state)
    document.querySelectorAll('.sk-recipe-card[data-recipe-id]').forEach(card => {
      const recipeId = card.dataset.recipeId;
      const countEl = card.querySelector('.sk-card__likes-count');
      if (countEl && liked[recipeId]) {
        const base = parseInt(countEl.textContent, 10) || 0;
        countEl.textContent = base + 1;
      }
    });
  }

  /* ================================================================
     SAVE TO KITCHEN
     ================================================================ */
  function initSaveToKitchen() {
    const btn = document.querySelector('.rd-btn--save');
    if (!btn) return;

    const wrapper = btn.closest('[data-recipe-id]');
    const recipeId = wrapper?.dataset.recipeId;
    if (!recipeId) return;

    const isLoggedIn = btn.dataset.loggedIn === 'true';
    const loginUrl = btn.dataset.loginUrl;

    const STORAGE_KEY = 'sk_saved';
    let saved = {};
    try { saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch (_) {}

    function updateSaveUI(isSaved) {
      btn.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
      btn.textContent = isSaved ? 'Saved ✓' : 'Save to my kitchen';
    }

    updateSaveUI(!!saved[recipeId]);

    btn.addEventListener('click', () => {
      if (!isLoggedIn && loginUrl) {
        window.location.href = loginUrl;
        return;
      }
      const next = !saved[recipeId];
      saved[recipeId] = next;
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(saved)); } catch (_) {}
      updateSaveUI(next);

      /*
       * Storefront API integration point:
       * To persist saved recipes to the customer account, configure a
       * customer metafield (namespace: "silva_kitchen", key: "saved_recipes",
       * type: "list.single_line_text") and call the Storefront API
       * customerUpdate mutation here with your storefront access token.
       * See the setup guide for full instructions.
       */
    });
  }

  /* ================================================================
     SHARE
     ================================================================ */
  function initShare() {
    const btn = document.querySelector('.rd-btn--share');
    if (!btn) return;

    btn.addEventListener('click', async () => {
      const title = document.querySelector('.rd-hero__title')?.textContent?.trim() || document.title;
      const url = window.location.href;

      if (navigator.share) {
        try {
          await navigator.share({ title, url });
        } catch (e) {
          if (e.name !== 'AbortError') copyUrl(btn, url);
        }
      } else {
        copyUrl(btn, url);
      }
    });
  }

  function copyUrl(btn, url) {
    if (!navigator.clipboard) return;
    const original = btn.textContent;
    navigator.clipboard.writeText(url).then(() => {
      btn.textContent = 'Link copied!';
      setTimeout(() => { btn.textContent = original; }, 2000);
    }).catch(() => {});
  }

  /* ================================================================
     PRINT
     ================================================================ */
  function initPrint() {
    const btn = document.querySelector('.rd-btn--print');
    if (!btn) return;
    btn.addEventListener('click', () => window.print());
  }

  /* ================================================================
     INIT
     ================================================================ */
  function init() {
    initFilterPills();
    initIotmCta();
    initServesAdjuster();
    initLikes();
    initSaveToKitchen();
    initShare();
    initPrint();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
